import { type BadgeOrderImageSource } from "@clipboard-health/contract-backend-main";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { openUrlInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { CbhIcon, PageFooter, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useRef } from "react";

import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { useCreateBadgeSmithOrder } from "../api/useCreateBadgeSmithOrder";
import { useSendWorkBadgeEmailWithPdf } from "../api/useSendWorkBadgeEmailWithPdf";
import { WorkBadgeEmailFailureBottomSheet } from "./WorkBadgeEmailFailureBottomSheet";
import { WorkBadgeEmailSuccessBottomSheet } from "./WorkBadgeEmailSuccessBottomSheet";
import { WorkBadgeOrderFailureBottomSheet } from "./WorkBadgeOrderFailureBottomSheet";
import { WorkBadgePreviewContainer } from "./WorkBadgePreviewContainer";

interface BadgeSmithCheckoutConfig {
  checkoutUrl: string;
}

interface WorkBadgesReadyProps {
  qualificationName: string;
  onBack: () => void;
  emailSuccessModal: {
    primaryButtonText: string;
    onPrimaryButtonClick: () => void;
  };
  workerHeadshotBase64: string;
  imageSource: BadgeOrderImageSource;
  badgeSmithCheckout?: BadgeSmithCheckoutConfig;
}

export function WorkBadgesReady(props: WorkBadgesReadyProps) {
  const {
    qualificationName,
    onBack,
    emailSuccessModal,
    workerHeadshotBase64,
    imageSource,
    badgeSmithCheckout,
  } = props;

  const worker = useDefinedWorker();

  const emailSuccessModalState = useModalState();
  const emailFailureModalState = useModalState();
  const orderFailureModalState = useModalState();

  const { mutateAsync: sendWorkBadgeEmailWithPdf, isLoading: isSendingBadgeEmail } =
    useSendWorkBadgeEmailWithPdf();

  const { mutateAsync: createBadgeSmithOrder, isLoading: isCreatingOrder } =
    useCreateBadgeSmithOrder();

  const handleSendEmail = useCallback(async () => {
    try {
      await sendWorkBadgeEmailWithPdf({
        qualificationName,
        workerHeadshotBase64,
      });

      emailSuccessModalState.openModal();
    } catch {
      emailFailureModalState.openModal();
    }
  }, [
    sendWorkBadgeEmailWithPdf,
    qualificationName,
    workerHeadshotBase64,
    emailSuccessModalState,
    emailFailureModalState,
  ]);

  const orderCreatedRef = useRef(false);

  const handleOrderBadge = useCallback(async () => {
    if (!badgeSmithCheckout) {
      return;
    }

    if (!orderCreatedRef.current) {
      try {
        await createBadgeSmithOrder({
          workerId: worker.userId,
          qualification: qualificationName,
          imageSource,
        });
        orderCreatedRef.current = true;
      } catch {
        orderFailureModalState.openModal();
        return;
      }
    }

    try {
      await openUrlInBrowser(badgeSmithCheckout.checkoutUrl, "badge-smith-checkout");
    } catch (error: unknown) {
      logError(APP_V2_APP_EVENTS.BADGE_SMITH_CHECKOUT_OPEN_FAILURE, {
        error,
        metadata: { workerId: worker.userId },
      });
      orderFailureModalState.openModal();
    }
  }, [
    badgeSmithCheckout,
    createBadgeSmithOrder,
    worker.userId,
    qualificationName,
    orderFailureModalState,
    imageSource,
  ]);

  const handleEmailRetry = useCallback(() => {
    emailFailureModalState.closeModal();
    void handleSendEmail();
  }, [emailFailureModalState, handleSendEmail]);

  const handleOrderRetry = useCallback(() => {
    orderFailureModalState.closeModal();
    void handleOrderBadge();
  }, [orderFailureModalState, handleOrderBadge]);

  return (
    <>
      <StickyScrollPageHeader
        title="Your Badge is Ready!"
        headerVariant="tertiary"
        onClickBack={onBack}
      />
      <PageContent>
        <Stack spacing={5}>
          <WorkBadgePreviewContainer
            qualificationName={qualificationName}
            workerHeadshotBase64={workerHeadshotBase64}
          />

          <Text bold variant="h6">
            Your badge looks great. Now let&apos;s get your hands on a physical version you can
            bring with you to your shifts!
          </Text>
        </Stack>
      </PageContent>

      <PageFooter>
        <Stack width="100%" spacing={5}>
          {/* TODO: Replace this with filled warning icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <CbhIcon type="triangle-exclamation" />
            <Text variant="body1">
              Many workplaces require a badge to be worn while working. Have your badge with you at
              all times so you are always prepared.
            </Text>
          </Stack>
          <Stack width="100%" spacing={3}>
            {badgeSmithCheckout && (
              <LoadingButton
                fullWidth
                size="large"
                isLoading={isCreatingOrder}
                disabled={isSendingBadgeEmail}
                onClick={handleOrderBadge}
              >
                Order a badge
              </LoadingButton>
            )}
            <LoadingButton
              fullWidth
              size="large"
              variant={badgeSmithCheckout ? "outlined" : "contained"}
              isLoading={isSendingBadgeEmail}
              disabled={isCreatingOrder}
              onClick={handleSendEmail}
            >
              Email a badge print-out
            </LoadingButton>
          </Stack>
        </Stack>
      </PageFooter>

      <WorkBadgeEmailSuccessBottomSheet
        modalState={emailSuccessModalState}
        primaryButtonText={emailSuccessModal.primaryButtonText}
        onPrimaryButtonClick={emailSuccessModal.onPrimaryButtonClick}
      />

      <WorkBadgeEmailFailureBottomSheet
        modalState={emailFailureModalState}
        onRetry={handleEmailRetry}
      />
      <WorkBadgeOrderFailureBottomSheet
        modalState={orderFailureModalState}
        onRetry={handleOrderRetry}
      />
    </>
  );
}
